/**
 * Backup file format and strict validation.
 *
 * Accepted inputs:
 *  - MEMOLINK backup v2   {format:"memolink-backup", version:2, data:{workspaces,categories,actions}, settings}
 *  - Prototype export     {format:"memolink-proto-export", version:1, data:<prototype state>}
 *  - Raw prototype state  {v:1, spaces:[{cats:[{actions:[…]}]}]}  (localStorage "memolink-proto-v1")
 * Anything else, or any invalid record, rejects the whole file. Nothing is changed on rejection.
 */
import { isColor, isIcon } from "./catalog";
import { normalizeUrl, nowIso } from "./format";
import { OPEN_WITH, SCHEMA_VERSION, type Action, type Category, type Library, type OpenWith, type Workspace } from "./types";

export const BACKUP_FORMAT = "memolink-backup";
export const PROTO_EXPORT_FORMAT = "memolink-proto-export";
export const MAX_BACKUP_BYTES = 5 * 1024 * 1024;
const MAX_ERRORS = 12;
const ID_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,119}$/;

export interface BackupFile {
  format: typeof BACKUP_FORMAT;
  version: number;
  app: "MEMOLINK";
  exportedAt: string;
  data: Pick<Library, "workspaces" | "categories" | "actions">;
  settings: Library["settings"];
}

export type BackupSource = "backup-v2" | "prototype-v1";
export type ParseResult =
  | { ok: true; source: BackupSource; library: Library; warnings: string[]; exportedAt?: string }
  | { ok: false; errors: string[] };

export function createBackup(lib: Library): BackupFile {
  return {
    format: BACKUP_FORMAT, version: SCHEMA_VERSION, app: "MEMOLINK", exportedAt: nowIso(),
    data: { workspaces: lib.workspaces, categories: lib.categories, actions: lib.actions },
    settings: lib.settings
  };
}

export const backupFileName = (d = new Date()) =>
  `memolink-backup-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}.json`;

export function parseBackup(text: string): ParseResult {
  if (typeof text !== "string" || !text.trim()) return { ok: false, errors: ["ไฟล์ว่าง ไม่มีข้อมูล"] };
  if (text.length > MAX_BACKUP_BYTES) return { ok: false, errors: ["ไฟล์ใหญ่เกิน 5 MB"] };
  let raw: unknown;
  try { raw = JSON.parse(text.replace(/^﻿/, "")); }
  catch { return { ok: false, errors: ["ไม่ใช่ไฟล์ JSON ที่อ่านได้ (รูปแบบเสีย)"] }; }
  return parseBackupObject(raw);
}

export function parseBackupObject(raw: unknown): ParseResult {
  if (!isObj(raw)) return { ok: false, errors: ["โครงสร้างไม่ถูกต้อง: ต้องเป็น JSON object"] };

  if (raw.format === BACKUP_FORMAT) {
    if (raw.version !== SCHEMA_VERSION) return { ok: false, errors: [`ไม่รองรับไฟล์สำรองเวอร์ชัน ${String(raw.version)} (รองรับเวอร์ชัน ${SCHEMA_VERSION})`] };
    if (!isObj(raw.data)) return { ok: false, errors: ["ไม่พบส่วน data ในไฟล์สำรอง"] };
    const r = validateLibrary(raw.data, raw.settings);
    return r.ok ? { ...r, source: "backup-v2", exportedAt: typeof raw.exportedAt === "string" ? raw.exportedAt : undefined } : r;
  }
  if (raw.format === PROTO_EXPORT_FORMAT) {
    const r = fromPrototype(raw.data);
    return r.ok ? { ...r, exportedAt: typeof raw.exportedAt === "string" ? raw.exportedAt : undefined } : r;
  }
  if (raw.v === 1 && Array.isArray(raw.spaces)) return fromPrototype(raw);
  return { ok: false, errors: ["ไม่ใช่ไฟล์สำรองของ MEMOLINK (ไม่พบ format: \"memolink-backup\")"] };
}

/* ---------- prototype (schema v1, nested) → v2 records ---------- */
function fromPrototype(p: unknown): ParseResult {
  if (!isObj(p) || p.v !== 1 || !Array.isArray(p.spaces)) return { ok: false, errors: ["ข้อมูลจากต้นแบบไม่ถูกต้อง: ไม่พบรายการพื้นที่ (spaces)"] };
  const errors: string[] = [];
  const workspaces: unknown[] = [], categories: unknown[] = [], actions: unknown[] = [];
  p.spaces.forEach((s: unknown, si: number) => {
    if (!isObj(s) || !Array.isArray(s.cats)) { errors.push(`spaces[${si}]: ไม่มีรายการหมวด`); return; }
    workspaces.push({ id: s.id, name: s.name, icon: s.icon, position: si, createdAt: s.created, updatedAt: s.updated });
    s.cats.forEach((c: unknown, ci: number) => {
      if (!isObj(c) || !Array.isArray(c.actions)) { errors.push(`spaces[${si}].cats[${ci}]: ไม่มีรายการ Action`); return; }
      categories.push({ id: c.id, workspaceId: s.id, name: c.name, icon: c.icon, glow: c.glow, position: ci, createdAt: c.created, updatedAt: c.updated });
      c.actions.forEach((a: unknown, ai: number) => {
        if (!isObj(a)) { errors.push(`spaces[${si}].cats[${ci}].actions[${ai}]: ไม่ใช่ object`); return; }
        actions.push({ id: a.id, categoryId: c.id, type: a.type, name: a.name, icon: a.icon, color: a.color,
          url: a.url ?? "", text: a.text ?? "", position: ai, createdAt: a.created, updatedAt: a.updated });
      });
    });
  });
  if (errors.length) return { ok: false, errors: errors.slice(0, MAX_ERRORS) };
  const r = validateLibrary({ workspaces, categories, actions }, { activeWorkspaceId: p.activeSpace, openWith: p.openWith });
  return r.ok ? { ...r, source: "prototype-v1" } : r;
}

/* ---------- record validation ---------- */
export function validateLibrary(data: unknown, settings: unknown):
  { ok: true; library: Library; warnings: string[] } | { ok: false; errors: string[] } {
  const errors: string[] = [], warnings: string[] = [];
  const err = (m: string) => { if (errors.length < MAX_ERRORS) errors.push(m); };
  if (!isObj(data)) return { ok: false, errors: ["ไม่พบข้อมูล"] };
  for (const k of ["workspaces", "categories", "actions"] as const)
    if (!Array.isArray(data[k])) err(`ไม่พบรายการ ${k}`);
  if (errors.length) return { ok: false, errors };

  const seen = new Map<string, string>();
  const base = (r: unknown, path: string, iconFallback: string) => {
    if (!isObj(r)) { err(`${path}: ไม่ใช่ object`); return null; }
    const id = r.id;
    if (typeof id !== "string" || !ID_RE.test(id)) { err(`${path}.id: ไม่มี ID หรือ ID ไม่ถูกต้อง`); return null; }
    if (seen.has(id)) { err(`${path}.id: ID "${id}" ซ้ำกับ ${seen.get(id)}`); return null; }
    seen.set(id, path);
    const name = typeof r.name === "string" ? r.name.trim() : "";
    if (!name) err(`${path}.name: ไม่มีชื่อ`);
    else if (name.length > 200) err(`${path}.name: ชื่อยาวเกิน 200 ตัวอักษร`);
    let icon = r.icon;
    if (!isIcon(icon)) { warnings.push(`${path}: ไม่รู้จักไอคอน "${String(icon)}" ใช้ไอคอนพื้นฐานแทน`); icon = iconFallback; }
    const createdAt = toIso(r.createdAt), updatedAt = toIso(r.updatedAt);
    if (!createdAt) err(`${path}.createdAt: วันที่สร้างไม่ถูกต้อง`);
    if (!updatedAt) err(`${path}.updatedAt: วันที่แก้ไขไม่ถูกต้อง`);
    const position = typeof r.position === "number" && Number.isFinite(r.position) ? r.position : NaN;
    if (Number.isNaN(position)) err(`${path}.position: ลำดับไม่ถูกต้อง`);
    if (r.sample !== undefined && typeof r.sample !== "boolean") err(`${path}.sample: ต้องเป็น true/false`);
    return { r, out: { id, name, icon: icon as string, position, createdAt: createdAt!, updatedAt: updatedAt!, ...(r.sample === true ? { sample: true } : {}) } };
  };

  const workspaces: Workspace[] = [];
  (data.workspaces as unknown[]).forEach((r, i) => { const b = base(r, `workspaces[${i}]`, "home"); if (b) workspaces.push(b.out); });
  const wsIds = new Set(workspaces.map(w => w.id));

  const categories: Category[] = [];
  (data.categories as unknown[]).forEach((r, i) => {
    const path = `categories[${i}]`, b = base(r, path, "folder"); if (!b) return;
    const workspaceId = b.r.workspaceId;
    if (typeof workspaceId !== "string" || !wsIds.has(workspaceId)) err(`${path}.workspaceId: ไม่พบพื้นที่ที่อ้างถึง`);
    let glow = b.r.glow;
    if (!isColor(glow)) { warnings.push(`${path}: ไม่รู้จักสี "${String(glow)}" ใช้สีม่วงแทน`); glow = "violet"; }
    categories.push({ ...b.out, workspaceId: workspaceId as string, glow: glow as string });
  });
  const catIds = new Set(categories.map(c => c.id));

  const actions: Action[] = [];
  (data.actions as unknown[]).forEach((r, i) => {
    const path = `actions[${i}]`, b = base(r, path, "link"); if (!b) return;
    const { categoryId, type } = b.r;
    if (typeof categoryId !== "string" || !catIds.has(categoryId)) err(`${path}.categoryId: ไม่พบหมวดที่อ้างถึง`);
    if (type !== "link" && type !== "memo") { err(`${path}.type: ต้องเป็น link หรือ memo`); return; }
    let color = b.r.color;
    if (!isColor(color)) { warnings.push(`${path}: ไม่รู้จักสี "${String(color)}" ใช้สีม่วงแทน`); color = "violet"; }
    const url = b.r.url ?? "", text = b.r.text ?? "";
    if (typeof url !== "string") { err(`${path}.url: ต้องเป็นข้อความ`); return; }
    if (typeof text !== "string") { err(`${path}.text: ต้องเป็นข้อความ`); return; }
    if (text.length > 20000) err(`${path}.text: Memo ยาวเกิน 20,000 ตัวอักษร`);
    let cleanUrl = "";
    if (type === "link" && url) {
      const n = normalizeUrl(url);
      if (!n.ok || !/^https?:\/\//i.test(url.trim())) err(`${path}.url: ลิงก์ต้องขึ้นต้นด้วย http:// หรือ https://`);
      else cleanUrl = n.url;
    }
    actions.push({ ...b.out, categoryId: categoryId as string, type, color: color as string,
      url: type === "link" ? cleanUrl : "", text: type === "memo" ? text : "" });
  });

  if (errors.length) return { ok: false, errors };

  const s = isObj(settings) ? settings : {};
  const openWith: OpenWith = OPEN_WITH.includes(s.openWith as OpenWith) ? (s.openWith as OpenWith) : "default";
  const activeWorkspaceId = typeof s.activeWorkspaceId === "string" && wsIds.has(s.activeWorkspaceId) ? s.activeWorkspaceId : (workspaces[0]?.id ?? null);
  return { ok: true, library: { workspaces, categories, actions, settings: { activeWorkspaceId, openWith } }, warnings };
}

function toIso(v: unknown): string | null {
  if (typeof v === "number" && Number.isFinite(v) && v > 0) return new Date(v).toISOString();
  if (typeof v === "string" && v) { const d = new Date(v); if (!Number.isNaN(d.getTime())) return d.toISOString(); }
  return null;
}
function isObj(v: unknown): v is Record<string, any> { return typeof v === "object" && v !== null && !Array.isArray(v); }
